import {useEffect, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router';
import {
  PasswordResetError,
  RESET_MESSAGES,
  confirmPasswordReset,
  tokenFromSearch,
  validateReset,
} from '../auth/passwordReset';
import AuthLayout from '../components/auth/AuthLayout';
import PasswordField from '../components/auth/PasswordField';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';
import './Login.css';

const ERROR_ID = 'update-error';
const EMPTY = {password: '', confirm_password: ''};
const IDS = {password: 'update-password', confirm_password: 'update-confirm-password'};
const errorId = (field) => `${IDS[field]}-error`;

/**
 * Sets a new password from the emailed link (/update-password?token=...). The token is single
 * use and expires after 10 minutes; success goes back to /login with a notice.
 */
export default function UpdatePassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const token = tokenFromSearch(location.search);
  const [values, setValues] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkState, setLinkState] = useState(token ? 'ok' : 'incomplete'); // 'ok' | 'incomplete' | 'invalid'

  // Focus follows the outcome: the new-password field when the link works, the message when not.
  useEffect(() => {
    const el = document.getElementById(linkState === 'ok' ? IDS.password : 'reset-link-error');
    if (el) el.focus();
  }, [linkState]);

  /** Focus the first invalid field, or the error text when the failure is not about a field. */
  const focusAfterError = (errors) => {
    const first = Object.keys(IDS).find((f) => errors[f]);
    const el = document.getElementById(first ? IDS[first] : ERROR_ID);
    if (el) el.focus();
  };

  const onChange = (event) => {
    const {name, value} = event.target;
    setValues((prev) => ({...prev, [name]: value}));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const errors = validateReset(values);
    if (Object.keys(errors).length) {
      // Field messages are shown inline and announced via aria-describedby; no summary repeat.
      setFieldErrors(errors);
      setError('');
      focusAfterError(errors);
      return;
    }
    setFieldErrors({});
    setError('');
    setBusy(true);
    try {
      await keys.run('reset-password', {token, password: values.password}, (idempotencyKey) =>
        confirmPasswordReset(token, values.password, {idempotencyKey}),
      );
    } catch (err) {
      setBusy(false);
      if (!(err instanceof PasswordResetError)) {
        setError(RESET_MESSAGES.unavailable);
        focusAfterError({});
        return;
      }
      if (err.code === 'invalid-token') {
        setLinkState('invalid');
        return;
      }
      const errs = err.fieldErrors || {};
      setFieldErrors(errs);
      // A field-level failure is shown inline; anything else goes to the summary alert.
      setError(Object.keys(errs).length ? '' : err.message);
      focusAfterError(errs);
      return;
    }
    navigate('/login', {replace: true, state: {notice: RESET_MESSAGES.updated}});
  };

  if (linkState !== 'ok') {
    const message = linkState === 'incomplete' ? RESET_MESSAGES.linkIncomplete : RESET_MESSAGES.linkInvalid;
    return (
      <AuthLayout>
        <div className="auth-heading auth-heading-tight">
          <p className="auth-eyebrow">ACCOUNT</p>
          <h1 className="auth-title">Link no longer works.</h1>
        </div>
        <div className="auth-panel auth-invalid-link">
          <p
            id="reset-link-error"
            className="auth-alert"
            role="alert"
            tabIndex={-1}
            data-testid="reset-link-error"
          >
            {message}
          </p>
          <p className="auth-switch">
            <Link to="/forget-password" className="auth-link">
              {RESET_MESSAGES.linkRequestNew}
            </Link>
          </p>
        </div>
      </AuthLayout>
    );
  }

  const describedBy = (field) => {
    const ids = [field === 'password' && 'update-password-hint', fieldErrors[field] && errorId(field)];
    return ids.filter(Boolean).join(' ') || undefined;
  };

  return (
    <AuthLayout>
      <div className="auth-heading auth-heading-tight">
        <p className="auth-eyebrow">ACCOUNT</p>
        <h1 className="auth-title">Choose a new password.</h1>
      </div>

      <div className="auth-panel">
        <form onSubmit={submit} noValidate aria-label="Choose a new password">
          <div className="auth-fields">
            <div>
              <PasswordField
                id={IDS.password}
                name="password"
                label="New password"
                toggleLabel="password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
                value={values.password}
                onChange={onChange}
                aria-invalid={fieldErrors.password ? 'true' : undefined}
                aria-describedby={describedBy('password')}
                data-testid="update-password"
              />
              <p id="update-password-hint" className="auth-hint">
                At least 8 characters, with a letter and a number.
              </p>
              {fieldErrors.password && (
                <p id={errorId('password')} className="auth-field-error">
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div>
              <PasswordField
                id={IDS.confirm_password}
                name="confirm_password"
                label="Confirm new password"
                toggleLabel="confirm password"
                placeholder="••••••••"
                autoComplete="new-password"
                required
                value={values.confirm_password}
                onChange={onChange}
                aria-invalid={fieldErrors.confirm_password ? 'true' : undefined}
                aria-describedby={describedBy('confirm_password')}
                data-testid="update-confirm-password"
              />
              {fieldErrors.confirm_password && (
                <p id={errorId('confirm_password')} className="auth-field-error">
                  {fieldErrors.confirm_password}
                </p>
              )}
            </div>
          </div>

          <div
            id={ERROR_ID}
            tabIndex={-1}
            className="auth-alert"
            role="alert"
            aria-atomic="true"
            data-testid="auth-error"
          >
            {error}
          </div>

          <button type="submit" className="auth-submit" disabled={busy} data-testid="update-submit">
            {busy ? 'Saving…' : 'Update password'}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
